'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/store/hooks'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdHealthAndSafety } from 'react-icons/md'
import {
    getRndsConfiguration,
    createRndsConfiguration,
    updateRndsConfiguration,
    CreateRndsConfigurationData,
} from '@/lib/api/health-integrations/rnds-api'

const UF_OPTIONS = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
]

export default function RndsFormPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { user } = useAuth()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)

    const [formData, setFormData] = useState<CreateRndsConfigurationData>({
        name: '',
        cnes: '',
        cnpj: '',
        uf: 'SP',
        active: true,
        environment: 'HOMOLOGATION',
        clientId: '',
        clientSecret: '',
        settings: {},
    })

    useEffect(() => {
        if (isEditing && id) {
            loadConfiguration(parseInt(id))
        }
    }, [id])

    const loadConfiguration = async (configId: number) => {
        setLoading(true)
        try {
            const response = await getRndsConfiguration(configId)
            if (response.success) {
                const config = response.result
                setFormData({
                    name: config.name,
                    cnes: config.cnes,
                    cnpj: config.cnpj,
                    uf: config.uf,
                    active: config.active,
                    environment: config.environment,
                    clientId: config.clientId || '',
                    clientSecret: '',
                    settings: config.settings || {},
                })
            } else {
                toast.error(response.error || 'Erro ao carregar configuração')
                navigate('/health-integrations/rnds')
            }
        } catch (error) {
            toast.error('Erro ao carregar configuração')
            navigate('/health-integrations/rnds')
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.name.trim()) {
            toast.error('Informe o nome da configuração')
            return
        }

        if (!formData.cnes.trim()) {
            toast.error('Informe o CNES')
            return
        }

        if (!formData.cnpj.trim()) {
            toast.error('Informe o CNPJ')
            return
        }

        setSaving(true)
        try {
            const data = { ...formData }
            if (!data.clientSecret) {
                delete (data as any).clientSecret
            }

            const response = isEditing
                ? await updateRndsConfiguration(parseInt(id!), data)
                : await createRndsConfiguration(data)

            if (response.success) {
                toast.success(
                    `Configuração ${isEditing ? 'atualizada' : 'criada'} com sucesso`
                )
                navigate('/health-integrations/rnds')
            } else {
                toast.error(
                    response.error ||
                    `Erro ao ${isEditing ? 'atualizar' : 'criar'} configuração`
                )
            }
        } catch (error) {
            toast.error(
                `Erro ao ${isEditing ? 'atualizar' : 'criar'} configuração`
            )
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando configuração...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/health-integrations/rnds')}
                    className="btn btn-ghost btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-lg">
                        <MdHealthAndSafety className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar' : 'Nova'} Configuração RNDS
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize os dados da configuração'
                                : 'Configure sua empresa para uso da RNDS'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Dados do Estabelecimento
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Nome da Configuração{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="Ex: UBS Central"
                                value={formData.name}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        name: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    CNPJ <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="00.000.000/0000-00"
                                value={formData.cnpj}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        cnpj: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    CNES <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="0000000"
                                value={formData.cnes}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        cnes: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    UF <span className="text-error">*</span>
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full focus:select-primary"
                                value={formData.uf}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        uf: e.target.value,
                                    })
                                }
                            >
                                {UF_OPTIONS.map(uf => (
                                    <option key={uf} value={uf}>
                                        {uf}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Ambiente
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full focus:select-primary"
                                value={formData.environment}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        environment: e.target.value as
                                            | 'PRODUCTION'
                                            | 'HOMOLOGATION',
                                    })
                                }
                            >
                                <option value="HOMOLOGATION">Homologação</option>
                                <option value="PRODUCTION">Produção</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-control mt-4">
                        <label className="label cursor-pointer justify-start gap-2">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-secondary"
                                checked={formData.active}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        active: e.target.checked,
                                    })
                                }
                            />
                            <span className="label-text">
                                Configuração ativa
                            </span>
                        </label>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Credenciais OAuth
                    </h2>
                    <div className="grid grid-cols-1 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Client ID
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="ID do cliente OAuth"
                                value={formData.clientId}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        clientId: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Client Secret
                                    {isEditing && ' (deixe vazio para manter)'}
                                </span>
                            </label>
                            <input
                                type="password"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="Segredo do cliente OAuth"
                                value={formData.clientSecret}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        clientSecret: e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="alert alert-info">
                    <span>
                        Para obter as credenciais OAuth da RNDS, acesse o Portal
                        de Serviços do DATASUS e solicite o cadastro do seu
                        estabelecimento.
                    </span>
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/health-integrations/rnds')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        {isEditing ? 'Salvar' : 'Criar'}
                    </button>
                </div>
            </form>
        </div>
    )
}
